import React from "react"
import styled from "styled-components"
import { em } from "polished"
import { capitalize } from "lodash"

import Link from "../link"

const DAYS = [
  `monday`,
  `tuesday`,
  `wednesday`,
  `thursday`,
  `friday`,
  `saturday`,
  `sunday`,
]

const Infobox = ({
  data,
  titles = {},
  translations,
  Anchor,
  Title,
  Styled,
  DocumentSvg,
  className,
}) => {
  if (!data) return null

  const groups = []

  if (data.address) {
    groups.push({
      id: `address`,
      title: titles?.address || translations.global.address,
      text: data.address,
    })
  }

  if (data.openingHours?.length) {
    groups.push({
      id: `openingHours`,
      title: titles?.openingHours || translations.global.openingHours,
      openingHours: data.openingHours,
    })
  }

  if (data.telephone) {
    groups.push({
      id: `telephone`,
      title: titles?.telephone || translations.global.telephone,
      line: `<a href="tel:${data.telephone}">${data.telephone}</a>`,
    })
  }

  if (data.email) {
    groups.push({
      id: `email`,
      title: titles?.email || translations.global.email,
      line: `<a href="mailto:${data.email}">${data.email}</a>`,
    })
  }

  if (data.difficultyText) {
    groups.push({
      id: `difficulty`,
      title: titles?.difficulty || translations.global.difficulty,
      line: data.difficultyText,
    })
  }

  if (data.length) {
    groups.push({
      id: `length`,
      title: titles?.length || translations.global.length,
      line: `${data.length} km`,
    })
  }

  if (data.time) {
    groups.push({
      id: `time`,
      title: titles?.time || translations.global.time,
      line: data.time,
    })
  }

  if (data.files?.length) {
    groups.push({
      id: `files`,
      title: titles?.files || translations.global.documents,
      documents: data.files.map((p) => ({ url: p.url, title: p.text })),
    })
  }

  if (data.links?.length) {
    groups.push({
      id: `links`,
      title: titles?.links || translations.global.website,
      links: data.links.map((l) => ({
        url: l.url,
        title: l.text,
      })),
    })
  }

  // if (data.facts) {
  //   groups.push({
  //     id: `facts`,
  //     title: translations.global.facts,
  //     text: data.facts,
  //   })
  // }

  // if (data.directions) {
  //   groups.push({
  //     id: `directions`,
  //     title: translations.global.directions,
  //     text: data.directions,
  //   })
  // }

  // if (data.surface) {
  //   groups.push({
  //     id: `surface`,
  //     title: translations.global.surface,
  //     text: data.surface,
  //   })
  // }

  // if (data.hills) {
  //   groups.push({
  //     id: `hills`,
  //     title: translations.global.hills,
  //     text: data.hills,
  //   })
  // }

  // if (data.vegetation) {
  //   groups.push({
  //     id: `vegetation`,
  //     title: translations.global.vegetation,
  //     text: data.vegetation,
  //   })
  // }

  // if (data.current) {
  //   groups.push({
  //     id: `current`,
  //     title: translations.global.current,
  //     text: data.current,
  //   })
  // }

  // if (data.parking) {
  //   groups.push({
  //     id: `parking`,
  //     title: translations.global.parking,
  //     text: data.parking,
  //   })
  // }

  // if (data.communications) {
  //   groups.push({
  //     id: `communications`,
  //     title: translations.global.communications,
  //     text: data.communications,
  //   })
  // }

  if (!groups.length) return null

  return (
    <Container className={className}>
      {groups.map((group) => (
        <React.Fragment key={group.id}>
          <Title as="h2" dangerouslySetInnerHTML={{ __html: group.title }} />

          {group.line && (
            <Text as={Styled}>
              <p dangerouslySetInnerHTML={{ __html: group.line }} />
            </Text>
          )}

          {group.text && (
            <Text
              as={Styled}
              dangerouslySetInnerHTML={{ __html: group.text }}
            />
          )}

          {group.links && (
            <Links>
              {group.links.map((link, i) => (
                <li key={i}>
                  <Anchor as={Link} to={link.url} target="_blank">
                    {link.title || translations.global.visit}
                  </Anchor>
                </li>
              ))}
            </Links>
          )}

          {group.documents && (
            <Documents>
              {group.documents.map((document, i) => (
                <li key={i}>
                  <Link to={document.url} target="_blank" download>
                    <DocumentSvg aria-hidden="true" />

                    <span
                      dangerouslySetInnerHTML={{
                        __html: document.title || translations.global.download,
                      }}
                    />
                  </Link>
                </li>
              ))}
            </Documents>
          )}

          {group.openingHours && (
            <OpeningHours>
              {group.openingHours.map((day, i) => (
                <React.Fragment key={i}>
                  <dt>
                    <Text as={Styled}>
                      <p>
                        {translations.global[DAYS[i]] || capitalize(DAYS[i])}
                      </p>
                    </Text>
                  </dt>

                  <dd>
                    <Text as={Styled}>
                      {day.open == `true` && (
                        <p>{translations.global.siteOpened || `Open 24h`}</p>
                      )}

                      {day.open == `false` && (
                        <p>{translations.global.siteClosed || `Closed`}</p>
                      )}

                      {day.open != `true` && day.open != `false` && (
                        <p>
                          {day.open}—{day.close}
                        </p>
                      )}
                    </Text>
                  </dd>
                </React.Fragment>
              ))}
            </OpeningHours>
          )}
        </React.Fragment>
      ))}
    </Container>
  )
}

export default Infobox

const Container = styled.section`
  padding: ${em(30)};
  color: ${({ theme }) => theme.components?.infobox?.color};
  background-color: ${({ theme }) =>
    theme.components?.infobox?.backgroundColor};
  border: 1px solid ${({ theme }) => theme.components?.infobox?.borderColor};
  border-radius: ${({ theme }) => theme.components?.infobox?.br};

  @media ${({ theme }) => theme.mq.xsmallDown} {
    padding: ${em(15)};
  }

  > h2 {
    margin-bottom: ${em(10)};

    &:not(:first-child) {
      margin-top: 1em;
      padding-top: 1em;
      border-top: 1px solid
        ${({ theme }) => theme.components?.infobox?.borderColor};
    }
  }
`

const Text = styled.div`
  font-size: ${em(14)};
`

const Links = styled.ul`
  li {
    + li {
      margin-top: ${em(4)};
    }
  }
`

const Documents = styled.ul`
  li {
    &:not(:last-child) {
      margin-bottom: ${em(8)};
    }
  }

  a {
    display: flex;
    align-items: baseline;
    gap: ${em(10)};

    &:hover {
      opacity: 0.8;
    }

    &:active {
      opacity: 0.6;
    }
  }

  svg {
    width: ${em(14)};
    height: ${em(14)};
    flex-shrink: 0;
    position: relative;
    bottom: ${em(-2)};
    fill: ${({ theme }) => theme.components?.infobox?.iconColor};
  }
`

const OpeningHours = styled.dl`
  display: flex;
  flex-wrap: wrap;

  dt,
  dd {
    width: 45%;

    &:not(:last-of-type) {
      margin-bottom: ${em(8)};
    }
  }

  dd {
    margin-left: 5%;
    text-align: right;
  }
`
